import { useMemo } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  CampaignCreate,
  CampaignPatch,
  CampaignPrivateView,
  CampaignPublicView,
} from "@bbb/model";
import {
  deleteCampaignApi,
  fetchPrivateCampaignsApi,
  fetchLiveCampaignsApi,
  saveCampaignApi,
  updateCampaignApi,
} from "@lib/apiClients";
import { getLatestCampaignStatus } from "@bbb/utils";
import { queryKeys } from "../lib/queryClient";
import { useAuthStore } from "./useAuthStore";

// #region Types and helpers

/**
 * Retrieve the current session auth context from the auth store.
 */
async function getAuthToken() {
  const { getSessionAuthToken } = useAuthStore.getState();
  return getSessionAuthToken();
}

export async function fetchPrivateCampaigns(
  communityId: string,
): Promise<CampaignPrivateView[]> {
  const token = await getAuthToken();
  const response = await fetchPrivateCampaignsApi(token, communityId);
  return response?.items ?? [];
}

async function fetchLiveCampaigns(
  communityId: string,
): Promise<CampaignPublicView[]> {
  const response = await fetchLiveCampaignsApi({ communityId });
  return response?.items ?? [];
}

/**
 * Fetch all live campaigns across communities (public view).
 * Paginates until the server stops returning a cursor.
 */
async function fetchAllLiveCampaigns(
  communityIds: string[],
): Promise<CampaignPublicView[]> {
  if (!communityIds.length) return [];

  const perCommunityPages = await Promise.all(
    communityIds.map(async (communityId) => {
      const campaigns: CampaignPublicView[] = [];
      let cursor: string | undefined;

      while (true) {
        const page = await fetchLiveCampaignsApi({ communityId, cursor });
        campaigns.push(...(page?.items ?? []));

        const nextCursor = page?.nextCursor;
        if (!nextCursor) {
          break;
        }

        cursor = nextCursor;
      }

      return campaigns;
    }),
  );

  return perCommunityPages.flat();
}
// #endregion

// #region Queries

export function usePrivateCampaigns(
  communityId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: communityId
      ? queryKeys.privateCampaigns(communityId)
      : ["campaigns", "missing-community"],
    queryFn: () => fetchPrivateCampaigns(communityId as string),
    enabled: enabled && Boolean(communityId),
  });
}

export function useLiveCampaigns(
  communityId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: communityId
      ? queryKeys.liveCampaigns(communityId)
      : ["campaigns-public", "missing-community"],
    queryFn: () => fetchLiveCampaigns(communityId as string),
    enabled: enabled && Boolean(communityId),
  });
}

/**
 * Fetch all live campaigns once and cache by a shared key.
 * Useful for guest / public dashboards to avoid N requests (one per community).
 */
export function useAllLiveCampaigns(enabled: boolean, communityIds: string[]) {
  return useQuery({
    queryKey: queryKeys.allLiveCampaigns(communityIds),
    queryFn: () => fetchAllLiveCampaigns(communityIds),
    enabled: enabled && communityIds.length > 0,
  });
}

// #endregion

// #region Mutations

/**
 * Create a campaign and invalidate the community campaigns list.
 * Writes the single-item cache immediately to avoid fetch gaps.
 */
export function useCreateCampaignMutation(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CampaignCreate) => {
      const token = await getAuthToken();
      return saveCampaignApi(token, { ...payload, communityId });
    },
    onSuccess: (campaign) => {
      if (campaign.id) {
        queryClient.setQueryData(queryKeys.theCampaign(campaign.id), campaign);
      }
      queryClient.setQueryData(
        queryKeys.privateCampaigns(communityId),
        (existing: CampaignPublicView[] | undefined) => {
          const items = existing ?? [];
          const withoutDuplicate = items.filter((c) => c.id !== campaign.id);
          return [...withoutDuplicate, campaign];
        },
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.privateCampaigns(communityId),
      });
    },
  });
}

/**
 * Update a campaign and invalidate the community campaigns list.
 * Writes the single-item cache immediately to reflect returned data.
 */
export function useUpdateCampaignMutation(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ updates }: { updates: CampaignPatch }) => {
      const token = await getAuthToken();
      return updateCampaignApi(token, { updates });
    },
    onSuccess: (campaign) => {
      if (campaign.id) {
        queryClient.setQueryData(queryKeys.theCampaign(campaign.id), campaign);
      }
      queryClient.setQueryData(
        queryKeys.privateCampaigns(communityId),
        (existing: CampaignPublicView[] | undefined) => {
          if (!existing || existing.length === 0) return existing;
          const found = existing.some((c) => c.id === campaign.id);
          if (!found) return existing;
          return existing.map((c) => (c.id === campaign.id ? campaign : c));
        },
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.privateCampaigns(communityId),
      });
    },
  });
}

/**
 * Delete a campaign and invalidate the community campaigns list.
 * Also removes the single-item cache entry.
 */
export function useDeleteCampaignMutation(communityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campaignId: string) => {
      const token = await getAuthToken();
      await deleteCampaignApi(token, campaignId);
      return campaignId;
    },
    onSuccess: (campaignId) => {
      queryClient.removeQueries({
        queryKey: queryKeys.theCampaign(campaignId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.privateCampaigns(communityId),
      });
    },
  });
}
// #endregion

// #region Derived helpers

/**
 * Return campaigns plus derived display-status groupings and flags.
 */
export function useCampaignDisplayStatuses(communityId: string | undefined) {
  const { data: campaigns = [], ...rest } = usePrivateCampaigns(communityId);

  const byStatus = useMemo(() => {
    return {
      draft: campaigns.filter((c) => getLatestCampaignStatus(c) === "draft"),
      scheduled: campaigns.filter(
        (c) => getLatestCampaignStatus(c) === "scheduled",
      ),
      live: campaigns.filter((c) => getLatestCampaignStatus(c) === "live"),
      ended: campaigns.filter((c) => getLatestCampaignStatus(c) === "ended"),
    };
  }, [campaigns]);

  const hasLive = byStatus.live;
  const hasEnded = byStatus.ended;

  return {
    campaigns,
    hasLive,
    hasEnded,
    byStatus,
    ...rest,
  };
}

/**
 * Fetch campaigns for many communities at once.
 */
export function useCampaignsForCommunities(communityIds: string[]) {
  const queries = useQueries({
    queries: communityIds.map((communityId) => ({
      queryKey: queryKeys.privateCampaigns(communityId),
      queryFn: () => fetchPrivateCampaigns(communityId),
      enabled: Boolean(communityId),
    })),
  });

  return useMemo(() => {
    return communityIds.reduce(
      (acc, communityId, index) => {
        const query = queries[index];
        acc[communityId] = {
          campaigns: (query?.data as CampaignPublicView[] | undefined) ?? [],
          isLoading: query?.isLoading ?? false,
          isFetching: query?.isFetching ?? false,
        };
        return acc;
      },
      {} as Record<
        string,
        {
          campaigns: CampaignPublicView[];
          isLoading: boolean;
          isFetching: boolean;
        }
      >,
    );
  }, [communityIds, queries]);
}

/**
 * Fetch live (public) campaigns for many communities at once.
 */
export function useLiveCampaignsForCommunities(communityIds: string[]) {
  const queries = useQueries({
    queries: communityIds.map((communityId) => ({
      queryKey: queryKeys.liveCampaigns(communityId),
      queryFn: () => fetchLiveCampaigns(communityId),
      enabled: Boolean(communityId),
    })),
  });

  return useMemo(() => {
    return communityIds.reduce(
      (acc, communityId, index) => {
        const query = queries[index];
        acc[communityId] = {
          campaigns: (query?.data as CampaignPublicView[] | undefined) ?? [],
          isLoading: query?.isLoading ?? false,
          isFetching: query?.isFetching ?? false,
        };
        return acc;
      },
      {} as Record<
        string,
        {
          campaigns: CampaignPublicView[];
          isLoading: boolean;
          isFetching: boolean;
        }
      >,
    );
  }, [communityIds, queries]);
}
// #endregion
